/**
 * The card the **kanban** and the **Cards** view both draw
 * (`specs/M4-V2-kanban.md` §2.1, `specs/M4-V7-gallery.md` §2.1).
 *
 * monday draws the same three pieces in both — `card-header` with the item name, the updates
 * indicator and the `···`, then one `single-cell-wrapper` per configured column, then an
 * optional cover band — at two different sets of metrics. One component with a `size` is
 * therefore not a resemblance, it is the measurement: a status pill on a kanban card and on a
 * gallery card are the same pixels by construction.
 *
 * Cells render through the **grid's own** registry (`getColumnUi(type).Cell`), so nothing here
 * knows what a status or a person looks like.
 */
import { Icon, icons } from '@elizmonday/ui';
import { memo, type CSSProperties, type HTMLAttributes, type MouseEvent as ReactMouseEvent, type Ref } from 'react';
import { getColumnUi } from '../../board-view/columns/registry';
import type { CellRenderContext } from '../../board-view/columns/types';
import type { BoardColumn, ItemRow } from '../../board-view/data/types';
import './cards.css';

export type CardTileSize = 'kanban' | 'gallery';

export interface ItemCardTileProps {
  item: ItemRow;
  /** The configured columns, already resolved from the view's `ColumnSelection`. */
  columns: readonly BoardColumn[];
  context: CellRenderContext;
  size: CardTileSize;
  /** `{ url, fit }` from the cover column's renderer, or `null` for no band. */
  cover?: { url: string; fit: 'cover' | 'contain' } | null;
  style?: CSSProperties;
  /**
   * Drag is **dnd-kit**'s (ADR-001), so the card takes the three things `useDraggable` hands
   * back rather than the HTML5 `draggable` / `onDragStart` pair it used in round 5u:
   * `setNodeRef` as `dragRef`, and `{...attributes, ...listeners}` as `dragProps`.
   * `draggable` is left as the visual/semantic flag (the `grab` cursor, `data-draggable`).
   */
  draggable?: boolean;
  dragRef?: Ref<HTMLElement>;
  dragProps?: HTMLAttributes<HTMLElement>;
  testIdPrefix?: string;
  onOpen(itemId: string): void;
  onMenu?(itemId: string, event: ReactMouseEvent): void;
}

function valueOf(item: ItemRow, columnId: string): unknown {
  return columnId === 'name' ? item.name : (item.values[columnId] ?? null);
}

export const ItemCardTile = memo(function ItemCardTile({
  item,
  columns,
  context,
  size,
  cover,
  style,
  draggable = false,
  dragRef,
  dragProps,
  testIdPrefix = 'card',
  onOpen,
  onMenu,
}: ItemCardTileProps) {
  return (
    <article
      {...dragProps}
      ref={dragRef}
      className="ez-card-tile"
      data-size={size}
      data-testid={`${testIdPrefix}-${item.id}`}
      data-item={item.id}
      data-draggable={draggable || undefined}
      style={style}
    >
      {cover ? (
        <div className="ez-card-tile__cover" data-testid={`${testIdPrefix}-${item.id}-cover`}>
          <img src={cover.url} alt="" style={{ objectFit: cover.fit }} />
        </div>
      ) : null}

      <header className="ez-card-tile__header">
        <button
          type="button"
          className="ez-card-tile__title"
          title={item.name}
          data-testid={`${testIdPrefix}-${item.id}-title`}
          onClick={() => onOpen(item.id)}
        >
          {item.name}
        </button>
        {item.updatesCount > 0 ? (
          <span
            className="ez-card-tile__updates"
            data-testid={`${testIdPrefix}-${item.id}-updates`}
            aria-label={`${String(item.updatesCount)} updates`}
          >
            <Icon icon={icons.update} size={16} ignoreFocusStyle />
            <b>{item.updatesCount}</b>
          </span>
        ) : null}
        {onMenu ? (
          <button
            type="button"
            className="ez-card-tile__menu"
            aria-label="Item actions"
            data-testid={`${testIdPrefix}-${item.id}-menu`}
            onClick={(event) => onMenu(item.id, event)}
          >
            <Icon icon={icons.moreActions} size={16} ignoreFocusStyle />
          </button>
        ) : null}
      </header>

      {columns.length > 0 ? (
        <div className="ez-card-tile__cells">
          {columns.map((column) => {
            const ui = getColumnUi(column.type);
            return (
              <div
                className="ez-card-tile__cell"
                key={column.id}
                data-column={column.id}
                data-testid={`${testIdPrefix}-${item.id}-cell-${column.id}`}
              >
                <span className="ez-card-tile__cell-label">
                  <Icon icon={icons[ui.icon]} size={16} ignoreFocusStyle />
                  <span>{column.title}</span>
                </span>
                <span className="ez-card-tile__cell-value" data-align={ui.align}>
                  <ui.Cell
                    value={valueOf(item, column.id)}
                    settings={column.settings}
                    column={column}
                    item={item}
                    context={context}
                  />
                </span>
              </div>
            );
          })}
        </div>
      ) : null}
    </article>
  );
});
